/**
 * Sprint 20 — HTTP client for /api/admin/cron-tasks/* admin endpoints.
 *
 * Replaces the Sprint 19-era SchedulersAdminApiClient. Same auth pattern:
 * Firebase ID token via `Authorization: Bearer`. Server-side `requireAdmin`
 * middleware enforces access.
 */

// ─── DTO types — mirror `src/modules/builder-service/src/cron-tasks/types.ts` ─────

export type CronTaskStatus = 'active' | 'paused' | 'archived';
export type CronTaskRunStatus =
  | 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type SchedulePreset = '@hourly' | '@daily' | '@weekly' | '@monthly';

export interface ScheduleDTO {
  preset?: SchedulePreset;
  intervalMinutes?: number;
  nextRunAt: string;
  lastRunAt?: string;
}

export interface PhaseStateDTO {
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'failed';
  startedAt?: string;
  completedAt?: string;
  meta?: Record<string, unknown>;
  error?: string;
}

export interface CronTaskDefinitionDTO {
  id: string;
  taskType: string;
  projectId: string | null;
  name: string;
  description?: string;
  schedule: ScheduleDTO;
  status: CronTaskStatus;
  payload: Record<string, unknown>;
  retentionPolicy?: { keepLastN?: number; keepYoungerThanDays?: number };
  retryPolicy?: Record<string, unknown>;
  lastRunId?: string;
  lastRunStatus?: CronTaskRunStatus;
  runsFired: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CronTaskRunDTO {
  id: string;
  taskId: string;
  taskType: string;
  projectId: string | null;
  status: CronTaskRunStatus;
  triggerReason: 'scheduled' | 'manual' | 'retry';
  attempts: number;
  phases: Record<string, PhaseStateDTO>;
  result?: Record<string, unknown>;
  error?: string;
  errorStack?: string;
  heartbeatAt?: string;
  payloadSnapshot: Record<string, unknown>;
  startedAt: string;
  completedAt?: string;
  updatedAt: string;
}

export interface CronTaskTickDTO {
  id: string;
  triggeredBy: 'cron' | 'manual';
  startedAt: string;
  completedAt?: string;
  elapsedMs: number;
  skipped: boolean;
  reason?: string;
  /** Absent on tick docs written before 2026-10-13. */
  role?: 'builder' | 'agent' | 'combined';
  tasksSkippedRemote?: number;
  tasksConsidered: number;
  runsCreated: number;
  zombiesSwept: number;
  failuresByType: Record<string, number>;
  failures: Array<{ taskId: string; taskType: string; projectId: string | null; error: string }>;
}

export interface KillSwitchStateDTO {
  enabled: boolean;
  enabledUntil?: string;
  setBy?: string;
  setAt?: string;
  reason?: string;
}

export interface AdminStatusResponse {
  success: boolean;
  countsByStatus?: Record<string, number>;
  countsByType?: Record<string, number>;
  runningNow?: number;
  lastTick?: CronTaskTickDTO | null;
  killSwitch?: KillSwitchStateDTO;
  knownTaskTypes?: string[];
  error?: string;
}

// ─── Client ──────────────────────────────────────────────────────────

export interface CronTasksAdminApiClientOptions {
  baseUrl?: string;
  getAuthToken?: () => Promise<string | null>;
}

export class CronTasksAdminApiClient {
  private readonly baseUrl: string;
  private readonly getAuthToken?: () => Promise<string | null>;

  constructor(opts: CronTasksAdminApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? '';
    this.getAuthToken = opts.getAuthToken;
  }

  status(): Promise<AdminStatusResponse> {
    return this.get('/api/admin/cron-tasks/status');
  }

  listDefinitions(filters: { taskType?: string; projectId?: string | null } = {}): Promise<{
    success: boolean;
    definitions?: CronTaskDefinitionDTO[];
    error?: string;
  }> {
    const params = new URLSearchParams();
    if (filters.taskType) params.set('taskType', filters.taskType);
    if (filters.projectId !== undefined) {
      params.set('projectId', filters.projectId === null ? 'null' : filters.projectId);
    }
    const qs = params.toString();
    return this.get(`/api/admin/cron-tasks/definitions${qs ? `?${qs}` : ''}`);
  }

  listRunning(): Promise<{ success: boolean; runs?: CronTaskRunDTO[]; error?: string }> {
    return this.get('/api/admin/cron-tasks/running');
  }

  listTicks(limit = 50): Promise<{ success: boolean; ticks?: CronTaskTickDTO[]; error?: string }> {
    return this.get(`/api/admin/cron-tasks/ticks?limit=${limit}`);
  }

  createTask(request: {
    taskType: string;
    projectId: string | null;
    name: string;
    description?: string;
    schedule: { preset?: SchedulePreset; intervalMinutes?: number; nextRunAt?: string };
    status?: CronTaskStatus;
    payload: Record<string, unknown>;
    retentionPolicy?: { keepLastN?: number; keepYoungerThanDays?: number };
    retryPolicy?: Record<string, unknown>;
    userId: string;
  }): Promise<{ success: boolean; task?: CronTaskDefinitionDTO; error?: string }> {
    return this.post('/api/admin/cron-tasks/tasks', request);
  }

  updateTask(
    id: string,
    patch: Partial<Pick<CronTaskDefinitionDTO, 'name' | 'description' | 'status' | 'retentionPolicy'>> & {
      schedule?: Partial<ScheduleDTO>;
      payload?: Record<string, unknown>;
      retryPolicy?: Record<string, unknown>;
    },
  ): Promise<{ success: boolean; task?: CronTaskDefinitionDTO; error?: string }> {
    return this.patch(`/api/admin/cron-tasks/tasks/${encodeURIComponent(id)}`, { patch });
  }

  deleteTask(id: string): Promise<{ success: boolean; error?: string }> {
    return this.delete(`/api/admin/cron-tasks/tasks/${encodeURIComponent(id)}`);
  }

  listTaskRuns(
    id: string,
    limit = 50,
  ): Promise<{ success: boolean; runs?: CronTaskRunDTO[]; error?: string }> {
    return this.get(`/api/admin/cron-tasks/tasks/${encodeURIComponent(id)}/runs?limit=${limit}`);
  }

  cancelRun(runId: string): Promise<{ success: boolean; flipped?: boolean; error?: string }> {
    return this.post(`/api/admin/cron-tasks/runs/${encodeURIComponent(runId)}/cancel`, {});
  }

  globalPause(minutes = 60, reason?: string): Promise<{
    success: boolean;
    state?: KillSwitchStateDTO;
    error?: string;
  }> {
    return this.post('/api/admin/cron-tasks/global-pause', { minutes, reason });
  }

  globalResume(): Promise<{ success: boolean; state?: KillSwitchStateDTO; error?: string }> {
    return this.post('/api/admin/cron-tasks/global-resume', {});
  }

  // ─── HTTP helpers ─────────────────────────────────────────────────

  private async get<T>(path: string): Promise<T> { return this.request('GET', path); }
  private async post<T>(path: string, body: unknown): Promise<T> { return this.request('POST', path, body); }
  private async patch<T>(path: string, body: unknown): Promise<T> { return this.request('PATCH', path, body); }
  private async delete<T>(path: string): Promise<T> { return this.request('DELETE', path); }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const hasBody = body !== undefined;
    // Only set Content-Type when actually sending a body. Edge-gateway
    // (Fastify) rejects Content-Type: application/json + empty body with
    // "Unexpected end of JSON input" 500s.
    const headers: Record<string, string> = hasBody
      ? { 'Content-Type': 'application/json' }
      : {};
    if (this.getAuthToken) {
      const token = await this.getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    const resp = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`CronTasksAdminApiClient: ${resp.status} ${resp.statusText} — ${text}`);
    }
    return resp.json() as Promise<T>;
  }
}
